import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";

// Context Providers
import { AuthProvider, useAuth } from "./context/AuthContext";
import { CartProvider } from "./context/CartContext";
import { LanguageProvider } from "./context/LanguageContext";

// Components
import { Navbar } from "./components/Navbar";
import { CartDrawer } from "./components/CartDrawer";
import { ActiveOrderBanner } from "./components/ActiveOrderBanner";
import TranslationNotice from "./components/TranslationNotice";

// Pages
import HomePage from "./pages/HomePage";
import RestaurantsPage from "./pages/RestaurantsPage";
import RestaurantDetailPage from "./pages/RestaurantDetailPage";
import CheckoutPage from "./pages/CheckoutPage";
import OrderTrackingPage from "./pages/OrderTrackingPage";
import ShishaRentalPage from "./pages/ShishaRentalPage";
import IceDeliveryPage from "./pages/IceDeliveryPage";
import ServicePage from "./pages/ServicePage";
import LoginPage from "./pages/LoginPage";
import AuthCallback from "./pages/AuthCallback";
import ProfilePage from "./pages/ProfilePage";
import AdminDashboard from "./pages/AdminDashboard";
import DriverDashboard from "./pages/DriverDashboard";
import RestaurantPOSDashboard from "./pages/RestaurantPOSDashboard";
import RestaurantPortalPage from "./pages/RestaurantPortalPage";
import SpecialMenuDetailPage from "./pages/SpecialMenuDetailPage";
import OrderSuccessPage from "./pages/OrderSuccessPage";

// Protected Route Component
const ProtectedRoute = ({ children, requiredRole }) => {
  const { user, loading, isAdmin, isDriver } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-[#FF6B35] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (requiredRole === 'admin' && !isAdmin) {
    return <Navigate to="/" replace />;
  }

  if (requiredRole === 'driver' && !isDriver) {
    return <Navigate to="/" replace />;
  }

  if (requiredRole === 'pos' && !['admin', 'super_admin', 'restaurant_manager'].includes(user.role)) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// App Router Component
const AppRouter = () => {
  const location = useLocation();

  // Check URL fragment for session_id (OAuth callback)
  // This must be checked synchronously during render, NOT in useEffect
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  // Check if we're on auth callback path
  if (location.pathname === '/auth/callback') {
    return <AuthCallback />;
  }

  // Don't show navbar on admin/driver/pos/restaurant-portal dashboards
  const hideNavbar = location.pathname.startsWith('/admin')
    || location.pathname.startsWith('/driver')
    || location.pathname.startsWith('/pos')
    || location.pathname.startsWith('/restaurant-portal');

  return (
    <>
      {!hideNavbar && <Navbar />}
      {!hideNavbar && <ActiveOrderBanner />}
      <CartDrawer />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/restaurants" element={<RestaurantsPage />} />
        <Route path="/restaurants/:id" element={<RestaurantDetailPage />} />
        <Route path="/special-menu/:id" element={<SpecialMenuDetailPage />} />
        <Route path="/shisha" element={<ShishaRentalPage />} />
        <Route path="/ice" element={<IceDeliveryPage />} />
        <Route path="/service/:serviceId" element={<ServicePage />} />
        <Route path="/checkout" element={<CheckoutPage />} />
        <Route path="/order-tracking/:id" element={<OrderTrackingPage />} />
        <Route path="/order-success" element={<OrderSuccessPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="/orders" element={<ProfilePage />} />
        <Route 
          path="/admin/*" 
          element={
            <ProtectedRoute requiredRole="admin">
              <AdminDashboard />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/driver/*" 
          element={
            <ProtectedRoute requiredRole="driver">
              <DriverDashboard />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/pos" 
          element={
            <ProtectedRoute requiredRole="pos">
              <RestaurantPOSDashboard />
            </ProtectedRoute>
          } 
        />
        <Route
          path="/restaurant-portal"
          element={
            <ProtectedRoute requiredRole="pos">
              <RestaurantPortalPage />
            </ProtectedRoute>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <LanguageProvider>
          <AuthProvider>
            <CartProvider>
              <AppRouter />
              <TranslationNotice />
              <Toaster 
                position="top-center" 
                richColors 
                toastOptions={{
                  style: {
                    borderRadius: '16px',
                  }
                }}
              />
            </CartProvider>
          </AuthProvider>
        </LanguageProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
